/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { KeyManager } from '../../../core/navigation';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { type TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import {
    onKeyBackChannelOptionsOverlay,
    onKeyOkChannelOptionsOverlayGuide,
    onKeyPlayChannelOptionsOverlay,
    onMouseClickChannelOptionsOverlay,
} from '../../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import {
    onKeyOkCreateOnePassOverlay,
    onMouseClickCreateOnePassOverlay,
} from '../../overlay/createonepassoverlay/createonepassoverlay.reducres';
import {
    onKeyBackExitConfirmationOverlay,
    onKeyOkExitConfirmationOverlay,
    onMouseClickExitConfirmationOverlay,
} from '../../overlay/exitconfirmationoverlay/exitconfirmationoverlay.reducers';
import {
    onKeyOkModifyOnePassOverlay,
    onMouseClickModifyOnePassOverlay,
} from '../../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import {
    onKeyBackNoNetworkOverlay,
    onKeyOkNoNetworkOverlay,
    onMouseClickNoNetworkOverlay,
} from '../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import {
    clearOnePassSharedPreference,
    onKeyLeftOnePassOptionsOverlay,
    onKeyOkDOCOverlay,
    onKeyOkOnePassOptionsOverlay,
    onKeyRightOnePassOptionsOverlay,
    onMouseClickDOCOptionsOverlay,
    onMouseClickOnePassOptionsOverlay,
} from '../../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import { OverlayTypes } from '../../overlay/OverlayTypes';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyLeftRecordingOverlay,
    onKeyOkRecordingOverlay,
    onKeyOkStopOrCancelRecording,
    onKeyRightRecordingOverlay,
    onMouseClickRecordingOverlay,
    onMouseClickStopOrCancelRecording,
} from '../../overlay/recordingoverlay/recordingoverlay.reducers';
import {
    onKeyBackRecordOverlay,
    onKeyOkRecordOverlay,
    onKeyPlayRecordOverlay,
    onMouseClickRecordOverlay,
} from '../../overlay/recordoverlay/recordoverlay.reducers';
import {
    onKeyBackSocuErrorOverlay,
    onKeyOkSocuErrorOverlay,
    onMouseClickSocuErrorOverlay,
} from '../../overlay/socuerroroverlay/socuerroroverlay.reducres';
import {
    onKeyOkStorageLimitOverlay,
    onMouseClickStorageLimitOverlay,
} from '../../overlay/storagelimitoverlay/storagelimitoverlay.reducers';
import {
    onKeyBackTemporaryGuideIssueOverlay,
    onKeyOkTemporaryGuideIssueOverlay,
    onMouseClickTemporaryGuideIssueOverlay,
} from '../../overlay/temporaryguideissueoverlay/temporaryguideissueoverlay.reducers';
import {
    onKeyBackVodResumeStartOverOverlay,
    onKeyOkVodResumeStartOverOverlay,
    onMouseClickVodResumeStartOverOverlay,
} from '../../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../overlaydialog/overlaydialog.reducers';
import { showMainSettingsScreen } from '../../screens/screens.reducers';
import { onKeyOkEnterChannelNumber } from '../enterchannelnumber/enterchannelnumber.reducers';
import { setGridGuideChannels } from '../guide.reducers';
import { startGuidePageScroll, startTurboScroll } from '../turboscroll/turboscroll.reducers';
import {
    cancelGuideSpinnerOperations,
    onKeyBackGuideOperations,
    onKeyDashGuideOperations,
    onKeyDigitGuideOperations,
    onKeyDownGuideOperations,
    onKeyInfoGuideOperations,
    onKeyLeftGuideOperations,
    onKeyOkGuideOperations,
    onKeyPlayGuideOperations,
    onKeyRightGuideOperations,
    onKeyUpGuideOperations,
    onLongKeyOkGuideOperations,
} from './guidekeyoperations.reducers';
import {
    onMouseClickGuideInteractiveLayerOperations,
    onMouseClickGuideOperations,
    onMouseOkLongPressStartGuideOperations,
    onMouseOkLongPressStartInteractiveLayerOperations,
    onMouseOkLongPressStopInteractiveLayerOperations,
    onMouseOutGuideOperations,
    onMouseOverGuideOperations,
    onMouseWheelGuideOperations,
} from './guidemouseoperations.reducers';

export const onKeyBackGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyBackGuideOperations());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyBackChannelOptionsOverlay());
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyBackGuideOperations());
            break;

        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
            dispatch(onKeyBackTemporaryGuideIssueOverlay());
            break;

        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onKeyBackExitConfirmationOverlay());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyBackNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onKeyBackRecordOverlay());
            break;
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onKeyBackSocuErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyBackVodResumeStartOverOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            clearOnePassSharedPreference();
            dispatch(hideOverlayDialog());
            break;

        default:
    }
};

export const onKeyRightGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyRightGuideOperations());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyRightOverlayDialog());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyRightGuideOperations());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyRightRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
            break;

        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyRightOnePassOptionsOverlay());
            break;

        default:
    }
};

export const onKeyLeftGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyLeftGuideOperations());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyLeftOverlayDialog());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyLeftGuideOperations());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyLeftRecordingOverlay());
            break;

        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
            break;

        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyLeftOnePassOptionsOverlay());
            break;

        default:
    }
};

export const onKeyDownGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyDownGuideOperations());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyDownGuideOperations());
            break;

        default:
    }
};

export const onKeyUpGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyUpGuideOperations());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyUpGuideOperations());
            break;

        default:
    }
};

export const onKeyOkGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyOkGuideOperations());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyOkChannelOptionsOverlayGuide());
            break;

        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
            dispatch(onKeyOkTemporaryGuideIssueOverlay());
            break;

        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onKeyOkExitConfirmationOverlay());
            break;
        case ScreenTaskControllerTypes.ENTER_CHANNEL_NUMBER:
            dispatch(onKeyOkEnterChannelNumber());
            break;
        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyOkGuideOperations());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyOkNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onKeyOkRecordOverlay());
            break;
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onKeyOkSocuErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyOkVodResumeStartOverOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkCreateOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkModifyOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onKeyOkStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyOkOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkDOCOverlay());
            break;
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;

        default:
    }
};

export const onKeyPlayGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyPlayGuideOperations());
            break;
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyPlayChannelOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyPlayGuideOperations());
            break;
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onKeyPlayRecordOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyOkVodResumeStartOverOverlay());
            break;

        default:
    }
};

export const onKeyPlayPauseGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            // If additional task needs to be performed at this point then create a reducer function
            // in channeloptionsoverlay.reducers as onKeyPlayPauseChannelOptionsOverlay.
            // For the time being using onKeyPlayChannelOptionsOverlay which serves the same purpose
            dispatch(onKeyPlayChannelOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            break;
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            // If additional task needs to be performed at this point then create a reducer function
            // in recordoverlay.reducers as onKeyPlayPauseRecordOverlay.
            // For the time being using onKeyPlayRecordOverlay which serves the same purpose
            dispatch(onKeyPlayRecordOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyOkVodResumeStartOverOverlay());
            break;

        default:
    }
};

export const onKeyDigitGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { uiProperties } = getState().overlayDialog;
    const { overlayType } = uiProperties;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;

        default:
    }
    if (overlayType !== OverlayTypes.PC_PIN) {
        dispatch(onKeyDigitGuideOperations());
    }
};

export const onKeyDashGuide = () => (dispatch: Function) => {
    dispatch(onKeyDashGuideOperations());
};

export const onKeyColorBlueGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const keyManagerInstance = KeyManager.getInstance();
    keyManagerInstance.startKeyIgnoring();
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(showMainSettingsScreen());

            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(showMainSettingsScreen());
            break;

        default:
            if (keyManagerInstance.isKeyIgnoring()) {
                keyManagerInstance.stopKeyIgnoring();
            }
    }
};

export const onKeyChannelDownGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { channels, LIMIT } = getState().gridGuide;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            if (channels.length < LIMIT) {
                dispatch(onKeyDownGuideOperations());
            } else {
                dispatch(setGridGuideChannels());
            }
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(setGridGuideChannels());
            break;

        default:
    }
};

export const onKeyChannelUpGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { channels, LIMIT } = getState().gridGuide;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            if (channels.length < LIMIT) {
                dispatch(onKeyUpGuideOperations());
            } else {
                dispatch(setGridGuideChannels(true));
            }

            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(setGridGuideChannels(true));
            break;

        default:
    }
};

export const onKeyInfoGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onKeyInfoGuideOperations());
            break;
        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onKeyInfoGuideOperations());
            break;
        default:
    }
};

export const onMouseClickGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.GUIDE_INTERACTIVE_LAYER_TASKS:
            dispatch(onMouseClickGuideInteractiveLayerOperations());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onMouseClickGuideOperations());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickDOCOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onMouseClickChannelOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickCreateOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onMouseClickExitConfirmationOverlay());
            break;
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickModifyOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onMouseClickOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
            dispatch(onMouseClickTemporaryGuideIssueOverlay());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onMouseClickNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onMouseClickRecordOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onMouseClickSocuErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onMouseClickVodResumeStartOverOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onMouseClickStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;

        default:
            break;
    }
};

export const onMouseOkLongPressStartGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.GUIDE_INTERACTIVE_LAYER_TASKS:
            dispatch(onMouseOkLongPressStartInteractiveLayerOperations());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onMouseOkLongPressStartGuideOperations());
            break;
        default:
    }
};

export const onMouseOkLongPressStopGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.GUIDE_INTERACTIVE_LAYER_TASKS:
            dispatch(onMouseOkLongPressStopInteractiveLayerOperations());
            break;
        default:
    }
};

export const onMouseOverGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onMouseOverGuideOperations());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;

        default:
    }
};

export const onMouseOutGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.GUIDE_INTERACTIVE_LAYER_TASKS:
            dispatch(onMouseOutGuideOperations());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.TEMPORARY_GUIDE_ISSUE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        default:
    }
};

export const onMouseWheelGuide =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
                dispatch(onMouseWheelGuideOperations(wheelDeltaY));
                break;
            default:
        }
    };

const onLongKeyChannelUpDownStartOperation =
    (isUpward: boolean = false) =>
    (dispatch: Function, getState: TGetState) => {
        const { gridGuide, screens } = getState();
        const { screenTaskController } = screens;
        const { channelsCount, LIMIT } = gridGuide;

        if (channelsCount <= LIMIT) {
            if (isUpward) {
                dispatch(onKeyChannelUpGuide());
            } else {
                dispatch(onKeyChannelDownGuide());
            }
            return;
        }

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
                dispatch(startGuidePageScroll(isUpward));
                break;

            case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
                dispatch(cancelGuideSpinnerOperations());
                dispatch(startGuidePageScroll(isUpward));
                break;

            default:
        }
    };

export const onLongKeyChannelDownStartGuide = () => (dispatch: Function) => {
    dispatch(onLongKeyChannelUpDownStartOperation());
};

export const onLongKeyChannelUpStartGuide = () => (dispatch: Function) => {
    dispatch(onLongKeyChannelUpDownStartOperation(true));
};

const onLongKeyUpDownStartOperation =
    (isUpward: boolean = false) =>
    (dispatch: Function, getState: TGetState) => {
        const { gridGuide, screens } = getState();
        const { screenTaskController } = screens;
        const { channelsCount, LIMIT } = gridGuide;

        if (channelsCount <= LIMIT) {
            if (isUpward) {
                dispatch(onKeyUpGuideOperations());
            } else {
                dispatch(onKeyDownGuideOperations());
            }
            return;
        }

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
                dispatch(startTurboScroll(isUpward));
                break;

            case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
                dispatch(cancelGuideSpinnerOperations());
                dispatch(startTurboScroll(isUpward));
                break;

            default:
        }
    };

export const onLongKeyDownStartGuide = () => (dispatch: Function) => {
    dispatch(onLongKeyUpDownStartOperation());
};

export const onLongKeyUpStartGuide = () => (dispatch: Function) => {
    dispatch(onLongKeyUpDownStartOperation(true));
};

export const onLongKeyOkGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(onLongKeyOkGuideOperations());
            break;

        case ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS:
            dispatch(cancelGuideSpinnerOperations());
            dispatch(onLongKeyOkGuideOperations());
            break;

        default:
    }
};
